import http from "@/service/http";
import store from "../store";
import dao from "@/model/generic_dao";

/**
 * Abstract class for any object representing a document in the database to
 * extend.
 */
class Entity {
  constructor({ id, restaurantId } = {}) {
    if (id) {
      this.id = id;
    }
    if (restaurantId) {
      this.restaurantId = restaurantId;
    }
  }

  /**
   * @returns A copy of this `Entity` as an instance of the same class.
   */
  clone() {
    return this.constructor._fromMap(this._toMap());
  }

  /**
   * Saves this `Entity` to the database.
   *
   * @returns {Promise<Entity>} Result of saving this `Entity`.
   */
  async save() {
    store.state.promiseLoading = true;
    if (this.collection) {
      let map = this._removeUndefinedValues(this._toMap());
      delete map.restaurantId;
      let response = await dao.save({ ...map, collection: this.collection });
      store.state.promiseLoading = false;
      return response;
    } else {
      let body = {};
      if (this._functionUrl.indexOf("restaurant") < 0) {
        body.restaurantId = store.getters.restaurant.id;
      }
      body[this._entityName] = { ...this._toMap() };
      let urlParts = [this.id];
      // console.log(`urlParts: ${JSON.stringify(urlParts)}`);
      if (this.id) {
        await http.put(this.functionUrl, {
          urlParts: urlParts,
          body: body
        });
      } else {
        await http.post(this.functionUrl, {
          urlParts: urlParts,
          body: body
        });
      }
    }
    store.state.promiseLoading = false;
  }
  /**
   * Deletes this `Entity` from the database.
   *
   * @returns {Promise<boolean>} If the operation was successful.
   * @prerrequisite `id` must exist.
   */
  async delete() {
    // TODO If not id, should throw error
    store.state.promiseLoading = true;
    if (this.collection) {
      dao.deleteById(this.collection, this.id);
    } else {
      await http.delete(this.functionUrl, {
        urlParts: [this.id],
        body: { restaurantId: store.getters.restaurant.id }
      });
    }
    store.state.promiseLoading = false;
  }

  /**
   *
   * @param {*} map Plain JS object representing an entity by this `Entity`.
   */
  static fromMap(map) {
    if (!this._fromMap) {
      throw new Error("Entity.fromMap Method should be overriden");
    }
    return this._fromMap(map);
  }

  /**
   * @returns {string} A `string` representation of the functionUrl to which
   * this `Entity` belongs to.
   */
  get functionUrl() {
    if (!this._functionUrl) {
      throw new Error(
        "Entity.collection, Attribute _functionUrl should be defined in subclass"
      );
    }
    return this._functionUrl;
  }

  _updateFromData(data) {
    let updatedEntity = this.constructor._fromMap(data);
    for (let key in updatedEntity) {
      this[key] = updatedEntity[key];
    }
  }

  get collection() {
    if (!this._collectionName) {
      return null;
    } else if (this._collectionName === "restaurants") {
      return `${this._collectionName}/`;
    }
    let collectionPath = "";
    let restaurantId = store.getters.restaurant.id;
    if (
      !this._collectionName.includes("restaurant") &&
      !this._collectionName.includes("locationCodes")
    ) {
      collectionPath = `restaurants/${restaurantId}/`;
    }
    // console.log(`${collectionPath}${this._collectionName}/`);
    return `${collectionPath}${this._collectionName}/`;
  }

  _removeUndefinedValues(obj) {
    if (obj instanceof Object && Object.keys(obj)) {
      for (let key in obj) {
        if (obj[key] !== undefined) {
          if (obj[key] instanceof Object && Object.keys(obj[key])) {
            obj[key] = this._removeUndefinedValues(obj[key]);
          }
        } else {
          delete obj[key];
        }
      }
      for (let key in obj) {
        obj[key] === undefined && delete obj[key];
      }
    }
    return obj;
  }

  static _fromMap() {
    throw new Error("Entity._fromMap, Method should be overriden");
  }

  _toMap() {
    throw new Error("Entity._toMap, Method should be overriden");
  }
}

export default Entity;
